export const MINDCLOUD_BRIDGE_EVENTS = {
  childReady: 'mindcloud:child-ready',
  childRequestAuth: 'mindcloud:request-auth',
  childRequestLogin: 'mindcloud:request-login',
  childRequestLogout: 'mindcloud:request-logout',
  childRequestOpenShare: 'mindcloud:request-open-share',
  childRequestOpenFeedback: 'mindcloud:request-open-feedback',
  childRequestNavigateBack: 'mindcloud:request-navigate-back',
  hostAuthState: 'mindcloud:auth-state',
  hostLoginResult: 'mindcloud:login-result',
  hostLocaleChange: 'mindcloud:host-locale-change',
  hostUnitSystemChange: 'mindcloud:host-unit-system-change',
} as const;

export type MindcloudBridgeEvent =
  (typeof MINDCLOUD_BRIDGE_EVENTS)[keyof typeof MINDCLOUD_BRIDGE_EVENTS];

export interface MindcloudAuthPayload {
  token?: string | null;
  user?: unknown;
  isAuthenticated?: boolean;
}

export interface MindcloudOpenSharePayload {
  target_id?: number;
  task_id?: number;
  task_code?: string;
  task?: unknown;
}

export interface MindcloudOpenFeedbackPayload {
  feedback?: {
    feedbackType?: 'bug' | 'feature' | 'experience' | 'performance';
    title?: string;
    content?: string;
  };
}

export interface MindcloudBridgeMessage<T = unknown> {
  type?: MindcloudBridgeEvent;
  payload?: T;
}

type ParentMessageHandler = (message: MindcloudBridgeMessage, event: MessageEvent) => void;

export function isParentBridgeAvailable(): boolean {
  try {
    return window.parent !== window;
  } catch {
    return false;
  }
}

function resolveParentTargetOrigin(): string {
  if (!document.referrer) return '*';
  try {
    return new URL(document.referrer).origin;
  } catch {
    return '*';
  }
}

export function postToParent(message: MindcloudBridgeMessage): boolean {
  if (!isParentBridgeAvailable()) return false;
  window.parent.postMessage(message, resolveParentTargetOrigin());
  return true;
}

export function subscribeParentMessages(handler: ParentMessageHandler): () => void {
  const listener = (event: MessageEvent) => {
    if (!isParentBridgeAvailable() || event.source !== window.parent) return;
    if (!event.data || typeof event.data !== 'object') return;
    handler(event.data as MindcloudBridgeMessage, event);
  };

  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
}

export function notifyExplorerReady(): boolean {
  return postToParent({ type: MINDCLOUD_BRIDGE_EVENTS.childReady });
}

export function requestParentAuth(): boolean {
  return postToParent({ type: MINDCLOUD_BRIDGE_EVENTS.childRequestAuth });
}

export function requestParentLogin(): boolean {
  return postToParent({ type: MINDCLOUD_BRIDGE_EVENTS.childRequestLogin });
}

export function requestParentLogout(): boolean {
  return postToParent({ type: MINDCLOUD_BRIDGE_EVENTS.childRequestLogout });
}

export function requestParentShare(payload: MindcloudOpenSharePayload): boolean {
  return postToParent({ type: MINDCLOUD_BRIDGE_EVENTS.childRequestOpenShare, payload });
}

export function requestParentFeedback(payload?: MindcloudOpenFeedbackPayload): boolean {
  return postToParent({ type: MINDCLOUD_BRIDGE_EVENTS.childRequestOpenFeedback, payload });
}

export function requestParentNavigateBack(): boolean {
  return postToParent({ type: MINDCLOUD_BRIDGE_EVENTS.childRequestNavigateBack });
}
